/**
 * A shipping-label QR encodes the public tracking URL for the item
 * (`https://…/track/{code}` where code = item_name or invoice). When a
 * hardware/camera scanner feeds that URL into a scan field, reduce it back to
 * the bare code the scan + lookup flows expect (they match on item_name /
 * invoice / waybill). A plain scanned code (no URL) passes through unchanged.
 */
export const trackCodeFromScan = (value: string): string => {
  const match = (value ?? '').match(/\/track\/([^/?#\s]+)/i);
  return (match ? decodeURIComponent(match[1]) : (value ?? '')).trim();
};

/**
 * Keystrokes closer together than this cannot come from a person. A scanner
 * gun is a keyboard-wedge device: it types the code one character at a time,
 * 1–10 ms apart (still under ~30 ms with an inter-character delay configured),
 * then presses Enter. Fast human typing sits at 60 ms+ between keys.
 */
export const SCANNER_BURST_MS = 50;

/**
 * Whether an input change came from a scanner gun rather than a person typing:
 * text was added (not deleted) and it arrived within SCANNER_BURST_MS of the
 * previous change. Evaluated per keystroke, so one slow gap mid-scan or one
 * fast key rollover by a person only affects that keystroke.
 */
export const isScannerKeystroke = (
  prevValue: string,
  nextValue: string,
  msSinceLastChange: number
): boolean =>
  nextValue.length > prevValue.length && msSinceLastChange < SCANNER_BURST_MS;

/**
 * Whether `value` is, or is on its way to being, a URL (`h`, `ht`, …,
 * `https://…`). Label QRs scan as tracking URLs, so this recognises a scan from
 * its very first characters, before the burst check has a previous keystroke to
 * compare against, and regardless of how fast the scanner types.
 */
export const looksLikeUrlInput = (value: string): boolean => {
  const v = (value ?? '').trim().toLowerCase();
  if (!v) return false;
  return 'https://'.startsWith(v) || /^https?:/.test(v);
};
